import { useFilterSheet } from '@/components/sheet/store/hooks/useFilterSheet.ts'
import { RowData } from '@flatfile/turntable'
import { useCallback, useContext, useRef, useState } from 'react'
import { FieldsContext } from '../contexts/FieldsContext.tsx'
import { downloadFile } from '../utils/downloadFile'
import { useSheetRecords } from './sheet/useSheetRecords.tsx'

//TODO: Replace with translations
const hardcoded = {
  error: 'Error downloading file.',
  success: 'File downloaded successfully.',
}

type SupportedFormats = 'csv' | 'json'

export const useDownload = (sourceSheetId: string, _fileId: string) => {
  const [isDownloading, setIsDownloading] = useState(false)
  const currentFormat = useRef<SupportedFormats>('csv')
  const { columnConfig } = useContext(FieldsContext)
  const { filters } = useFilterSheet(sourceSheetId)
  const { fetchRecords, commitCurrentChanges } = useSheetRecords()

  const transformToJSON = useCallback(
    (records: RowData[], columnConfig: any[]) => {
      // Create a column mapping for quick lookups
      const columnMapping = columnConfig.reduce(
        (mapping, column, index) => {
          mapping[index] = column.value
          return mapping
        },
        {} as Record<number, string>
      )

      // Process records into a clean JSON structure
      const jsonData = records.map((record, recordIndex) => {
        const jsonRecord: Record<string, any> = {}

        // Fill in the values from the record's cells
        if (record && record.cells && Array.isArray(record.cells)) {
          record.cells.forEach((cell, index) => {
            if (cell && typeof cell === 'object') {
              const fieldName = columnMapping[index]
              jsonRecord[fieldName] = cell.diffValue ?? cell.value ?? ''
            }
          })
        }

        return jsonRecord
      })

      // Convert to JSON string with pretty printing for readability
      return JSON.stringify(jsonData, null, 2)
    },
    []
  )

  const transformToCSV = useCallback(
    (records: RowData[], columnConfig: any[]) => {
      const headers = columnConfig.map((column) => column.label)
      const csvData = [
        headers,
        ...records.map((record) => {
          return record.cells.map((c) =>
            c.diffValue ? `"${c.diffValue}"` : c.value ? `"${c.value}"` : ''
          )
        }),
      ]
      return csvData.map((row) => row.join(',')).join('\n')
    },
    []
  )

  const handleExport = useCallback(async () => {
    try {
      await commitCurrentChanges(columnConfig as any) // TODO: Uncomment this when we have a way to commit changes with blueprint updates
      const records = await fetchRecords(
        { searchFields: filters, mode: 'export' } as any, // TODO fix this type,
        columnConfig as any
      )

      if (currentFormat.current === 'csv') {
        const csvData = transformToCSV(records, columnConfig)
        downloadFile({
          fileName: `${sourceSheetId}.csv`,
          file: new Blob([csvData], { type: 'text/csv' }),
        })
      } else if (currentFormat.current === 'json') {
        const jsonData = transformToJSON(records, columnConfig)
        downloadFile({
          fileName: `${sourceSheetId}.json`,
          file: new Blob([jsonData], { type: 'application/json' }),
        })
      }
    } finally {
      setIsDownloading(false)
    }
  }, [
    columnConfig,
    fetchRecords,
    filters,
    sourceSheetId,
    transformToCSV,
    transformToJSON,
    commitCurrentChanges,
  ])

  const handleDownload = useCallback(
    (format: SupportedFormats) => {
      setIsDownloading(true)
      currentFormat.current = format
      setTimeout(() => {
        handleExport()
      }, 0)
    },
    [handleExport]
  )

  return {
    handleDownload,
    isDownloading,
  }
}
